/**
 * @fileoverview CodeMirror panel extension for AI toolbar.
 * Fixed panel at top of editor - no floating, no drag needed.
 * AI body text is previewed in-editor as a ghost; this chrome shows
 * status, collapsible thinking, and Accept/Reject.
 * @module @editor/extensions/aiToolbarPanel
 */

import { EditorView, showPanel, type Panel, ViewPlugin, ViewUpdate } from '@codemirror/view';
import { SelectionRange, StateEffect } from '@codemirror/state';
import type { AIOperation, ToolbarConfig } from '../../db/characterTypes';
import { DEFAULT_TOOLBAR_CONFIG, normalizeToolbarConfig } from '../../db/characterTypes';
import {
  resolveToolbarButtons,
  toolbarButtonLabel,
  type ToolbarButtonDef,
} from '../../services/toolbarConfig';
import { toggleToolbarSearch, searchPanelOpen, closeToolbarSearch } from './toolbarSearch';
import { createFontSizeControl } from './fontSizeControl';
import { clipLiveReasoning } from '../../components/ai/utils';
import { AIService } from '../../services/AIService';
import type { SamplerSettings } from '../../db/characterTypes';
import { isImeComposing } from '../../utils/imeComposing';

/**
 * Callback for AI operations
 */
export type AIToolbarActionCallback = (
  operation: AIOperation,
  selectedText: string,
  selection: SelectionRange,
  customPrompt?: string
) => void;

/**
 * Callback for streaming updates
 */
export type AIStreamingCallback = (update: {
  isProcessing?: boolean;
  isStreaming?: boolean;
  streamingContent?: string;
  streamingReasoning?: string;
  aiResult?: string | null;
  aiReasoning?: string;
  currentOperation?: AIOperation | null;
  error?: string | null;
  instructPrompt?: string | null;
  stats?: { ttft?: number; tokensPerSecond?: number; modelId?: string; providerId?: string; truncated?: boolean };
}) => void;

/**
 * Callback for aborting AI operations
 */
export type AIAbortCallback = () => void;

/**
 * Re-run the current op on the same range. `editedPrompt` replaces the Custom
 * instruction when the user changed it in the reopened box.
 */
export type AIRetryCallback = (editedPrompt?: string) => void;

/**
 * Callback for font size changes
 */
export type FontSizeChangeCallback = (size: number) => void;

/**
 * Callback to open a preflight AI payload preview for the current selection
 */
export type AIPreviewPayloadCallback = (payload: {
  text: string;
  selection: SelectionRange;
  defaultOperation: AIOperation;
  instructDraft?: string;
}) => void;

export interface ToolbarActionConfig {
  id: string;
  label: string;
  title?: string;
  onClick: (view: EditorView) => void;
}

/** `org/Model-Name:variant` → `Model-Name:variant` for the compact model chip. */
function shortModelName(modelId: string): string {
  const slash = modelId.lastIndexOf('/');
  return slash >= 0 && slash < modelId.length - 1 ? modelId.slice(slash + 1) : modelId;
}

const MODEL_ICON_PATHS = [
  'M6 4h12a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z',
  'M10 9h4a1 1 0 0 1 1 1v4a1 1 0 0 1-1 1h-4a1 1 0 0 1-1-1v-4a1 1 0 0 1 1-1z',
  'M9 2v2', 'M15 2v2', 'M9 20v2', 'M15 20v2', 'M2 9h2', 'M2 15h2', 'M20 9h2', 'M20 15h2',
];
const TIMER_ICON_PATHS = ['M10 2h4', 'M12 14l3-3', 'M4 14a8 8 0 1 0 16 0a8 8 0 1 0-16 0'];
const GAUGE_ICON_PATHS = ['m12 14 4-4', 'M3.34 19a10 10 0 1 1 17.32 0'];
const HISTORY_ICON_PATHS = ['M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8', 'M3 3v5h5', 'M12 7v5l4 2'];

function createIcon(paths: string[], size: number): SVGSVGElement {
  const icon = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  icon.setAttribute('viewBox', '0 0 24 24');
  icon.setAttribute('fill', 'none');
  icon.setAttribute('stroke', 'currentColor');
  icon.setAttribute('stroke-width', '2');
  icon.setAttribute('stroke-linecap', 'round');
  icon.setAttribute('stroke-linejoin', 'round');
  icon.setAttribute('aria-hidden', 'true');
  icon.style.cssText = `width: ${size}px; height: ${size}px; flex-shrink: 0;`;
  for (const d of paths) {
    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path.setAttribute('d', d);
    icon.appendChild(path);
  }
  return icon;
}

const RECENT_INSTRUCTIONS_KEY = 'cv-ai-toolbar-recent-instructions';
const MAX_RECENT_INSTRUCTIONS = 10;

/** Newest first; shared by every editor's Custom box. */
function getRecentInstructions(): string[] {
  try {
    const parsed: unknown = JSON.parse(localStorage.getItem(RECENT_INSTRUCTIONS_KEY) ?? '[]');
    return Array.isArray(parsed)
      ? parsed.filter((p): p is string => typeof p === 'string' && p.trim() !== '').slice(0, MAX_RECENT_INSTRUCTIONS)
      : [];
  } catch {
    return [];
  }
}

function saveRecentInstructions(list: string[]): void {
  try {
    localStorage.setItem(RECENT_INSTRUCTIONS_KEY, JSON.stringify(list));
  } catch {
    // Storage may be unavailable; recent instructions are best-effort.
  }
}

function rememberInstruction(prompt: string): void {
  saveRecentInstructions(
    [prompt, ...getRecentInstructions().filter((p) => p !== prompt)].slice(0, MAX_RECENT_INSTRUCTIONS),
  );
}

function forgetInstruction(prompt: string): void {
  saveRecentInstructions(getRecentInstructions().filter((p) => p !== prompt));
}

/** Rounded icon + label pill for the result header (model, timing). */
function createStatPill(iconPaths: string[], color: string): { pill: HTMLSpanElement; label: HTMLSpanElement } {
  const pill = document.createElement('span');
  pill.className = 'ai-toolbar-stat-pill';
  pill.style.cssText = `
    display: none;
    align-items: center;
    gap: 4px;
    min-width: 0;
    padding: 1px 8px 1px 6px;
    font-size: 11px;
    font-weight: 500;
    line-height: 16px;
    font-variant-numeric: tabular-nums;
    color: ${color};
    background: var(--ai-toolbar-btn-hover);
    border: 1px solid var(--ai-toolbar-input-border);
    border-radius: 999px;
    white-space: nowrap;
    cursor: default;
  `;
  const icon = createIcon(iconPaths, 12);
  icon.style.opacity = '0.8';
  const label = document.createElement('span');
  label.style.cssText = 'overflow: hidden; text-overflow: ellipsis;';
  pill.append(icon, label);
  return { pill, label };
}

// Registry to store panel update functions by editor view
const panelRegistry = new WeakMap<EditorView, AIStreamingCallback>();

/**
 * Get the panel's updateAIState function for a given editor view
 */
export function getPanelUpdateFunction(view: EditorView): AIStreamingCallback | undefined {
  return panelRegistry.get(view);
}

/**
 * Panel state for AI operations
 */
interface AIPanelState {
  isProcessing: boolean;
  isStreaming: boolean;
  streamingContent: string;
  streamingReasoning: string;
  aiResult: string | null;
  aiReasoning: string;
  currentOperation: AIOperation | null;
  error: string | null;
  instructPrompt: string | null;
  stats: { ttft?: number; tokensPerSecond?: number; modelId?: string; providerId?: string; truncated?: boolean } | null;
}

/**
 * Create the AI toolbar panel DOM element
 */
function createToolbarPanel(
  view: EditorView,
  sampler: SamplerSettings,
  onAction: AIToolbarActionCallback,
  onAccept: () => void,
  onReject: () => void,
  onAbort: AIAbortCallback,
  onRetry: AIRetryCallback,
  onFontSizeChange?: FontSizeChangeCallback,
  toolbarActions: ToolbarActionConfig[] = [],
  onPreviewPayload?: AIPreviewPayloadCallback,
  initialToolbarConfig: ToolbarConfig = DEFAULT_TOOLBAR_CONFIG,
): Panel & { updateState: () => void; updateAIState: AIStreamingCallback; updateSampler: (s: SamplerSettings) => void; updateToolbarConfig: (c: ToolbarConfig) => void } {
  const dom = document.createElement('div');
  dom.className = 'ai-toolbar-panel';

  // Styles
  dom.style.cssText = `
    display: flex;
    flex-direction: column;
    background: var(--ai-toolbar-bg);
    border-bottom: 1px solid var(--ai-toolbar-border);
  `;

  // AI Panel State
  const state: AIPanelState = {
    isProcessing: false,
    isStreaming: false,
    streamingContent: '',
    streamingReasoning: '',
    aiResult: null,
    aiReasoning: '',
    currentOperation: null,
    error: null,
    instructPrompt: null,
    stats: null,
  };
  
  let currentSampler = sampler;
  const SAFETY_MARGIN = 256; // Match AIService.SAFETY_MARGIN

  const toolbarContainer = document.createElement('div');
  toolbarContainer.className = 'ai-toolbar-controls';
  toolbarContainer.style.cssText = `
    position: relative;
    display: flex;
    flex-wrap: nowrap;
    align-items: center;
    gap: 8px;
    padding: 8px 12px;
    min-height: 44px;
  `;
  dom.appendChild(toolbarContainer);

  // Compact result chrome (thinking + actions). AI body text is an in-editor ghost.
  const resultContainer = document.createElement('div');
  resultContainer.className = 'ai-result-container';
  resultContainer.style.cssText = `
    display: none;
    flex-direction: column;
    padding: 8px 12px;
    border-top: 1px solid var(--ai-toolbar-border);
    background: var(--ai-toolbar-result-bg);
    max-height: min(40vh, 220px);
    overflow-y: auto;
  `;
  dom.appendChild(resultContainer);

  // Toolbar buttons resolve from the user-configured order (builtins + custom ops).
  // `instruct` (Custom) is always pinned by normalizeToolbarConfig.
  let toolbarConfig = normalizeToolbarConfig(initialToolbarConfig);

  let hasSelection = false;
  let currentSelection: SelectionRange | null = null;
  let selectedText = '';
  let isInstructMode = false;
  let hasSamplerError = false;
  /** Set in destroy() so delayed rAF focus work cannot touch a dead view. */
  let panelDestroyed = false;
  /** Pending ensureEditorFocus / instruct focus frames — cancelled on destroy. */
  const pendingFocusRafs = new Set<number>();

  function scheduleFocusWork(work: () => void) {
    if (panelDestroyed) return;
    const id = requestAnimationFrame(() => {
      pendingFocusRafs.delete(id);
      if (panelDestroyed) return;
      work();
    });
    pendingFocusRafs.add(id);
  }

  /**
   * Keep keyboard shortcuts / Accept working after toolbar buttons are hidden.
   * Hiding the focused button (processing UI swap) drops focus to <body>, which
   * used to disable Mod-Enter / Escape until the user clicked the editor again.
   */
  function ensureEditorFocus() {
    if (panelDestroyed) return;
    // Leave the custom-instruction box alone while the user is typing there
    if (document.activeElement === instructInput) return;
    try {
      view.focus();
    } catch {
      // view may be mid-destroy
    }
  }

  /**
   * Whether this panel should handle Accept/Reject shortcuts right now: focus is
   * in this editor (panel included), or fell to <body> with no modal open.
   * Focus anywhere else (chat, settings, history, another editor) keeps its keys.
   */
  function shouldHandleAiShortcuts(): boolean {
    const hasSession =
      state.isProcessing || state.isStreaming || !!state.aiResult || !!state.error;
    if (!hasSession) return false;

    const active = document.activeElement;
    if (active && view.dom.contains(active)) return true;
    if (!active || active === document.body) {
      return !document.querySelector('[aria-modal="true"]');
    }
    return false;
  }

  // Create button helper (uniform styling; overflow placement is handled by relayout)
  function createButton(def: ToolbarButtonDef): HTMLButtonElement {
    const btn = document.createElement('button');
    btn.className = `ai-toolbar-btn ai-toolbar-btn-${def.id}`;
    btn.dataset.opId = def.id;
    const iconSpan = document.createElement('span');
    iconSpan.style.marginRight = '4px';
    iconSpan.textContent = def.icon;
    const labelSpan = document.createElement('span');
    labelSpan.className = 'ai-toolbar-btn-label';
    labelSpan.textContent = def.label;
    btn.append(iconSpan, labelSpan);
    if (def.label.length > 12) btn.title = def.label;
    btn.style.cssText = `
      display: flex;
      align-items: center;
      padding: 6px 12px;
      font-size: 13px;
      font-weight: 500;
      color: white;
      background: ${def.color};
      border: none;
      border-radius: 6px;
      cursor: pointer;
      opacity: 0.5;
      pointer-events: none;
      transition: opacity 0.2s, transform 0.1s;
      white-space: nowrap;
      flex-shrink: 0;
    `;

    btn.addEventListener('click', () => {
      if (hasSelection && currentSelection) {
        dropdown.style.display = 'none';
        onAction(def.id, selectedText, currentSelection);
        // Focus after the processing UI swaps (hidden button would otherwise blur to body)
        scheduleFocusWork(() => ensureEditorFocus());
      }
    });

    btn.addEventListener('mousedown', (e) => {
      e.preventDefault(); // Keep editor focused instead of focusing the button
    });

    return btn;
  }

  // Custom instruction input (hidden by default)
  const instructContainer = document.createElement('div');
  instructContainer.className = 'ai-toolbar-instruct';
  instructContainer.style.cssText = 'display: none; align-items: center; gap: 6px; flex: 1; min-width: 200px;';

  const instructInput = document.createElement('textarea');
  instructInput.className = 'ai-toolbar-instruct-input';
  instructInput.placeholder = 'What would you like me to do?';
  instructInput.rows = 1;
  instructInput.style.cssText = `
    flex: 1;
    padding: 6px 10px;
    font-size: 13px;
    background: var(--ai-toolbar-input-bg);
    border: 1px solid var(--ai-toolbar-input-border);
    border-radius: 6px;
    color: var(--ai-toolbar-text);
    outline: none;
    resize: none;
    overflow-y: auto;
    min-height: 34px;
    max-height: 120px;
    font-family: inherit;
    line-height: 1.4;
  `;

  const instructSendBtn = document.createElement('button');
  instructSendBtn.className = 'ai-toolbar-instruct-send';
  instructSendBtn.innerHTML = 'Send';
  instructSendBtn.style.cssText = `
    padding: 6px 12px;
    font-size: 13px;
    font-weight: 500;
    color: white;
    background: var(--ai-toolbar-accent-success);
    border: none;
    border-radius: 6px;
    cursor: pointer;
  `;

  const instructCancelBtn = document.createElement('button');
  instructCancelBtn.className = 'ai-toolbar-instruct-cancel';
  instructCancelBtn.innerHTML = 'Cancel';
  instructCancelBtn.style.cssText = `
    padding: 6px 10px;
    font-size: 12px;
    color: var(--ai-toolbar-text-secondary);
    background: transparent;
    border: 1px solid var(--ai-toolbar-input-border);
    border-radius: 6px;
    cursor: pointer;
  `;

  const recentBtn = document.createElement('button');
  recentBtn.type = 'button';
  recentBtn.className = 'ai-toolbar-instruct-recent';
  recentBtn.title = 'Recent instructions (↑ in an empty box)';
  recentBtn.setAttribute('aria-label', 'Recent instructions');
  recentBtn.setAttribute('aria-haspopup', 'menu');
  recentBtn.setAttribute('aria-expanded', 'false');
  recentBtn.style.cssText = `
    display: none;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 32px;
    height: 32px;
    padding: 0;
    color: var(--ai-toolbar-text-secondary);
    background: transparent;
    border: 1px solid var(--ai-toolbar-input-border);
    border-radius: 6px;
    cursor: pointer;
  `;
  recentBtn.appendChild(createIcon(HISTORY_ICON_PATHS, 14));

  // Recent instructions menu, dropped under the controls row
  const recentMenu = document.createElement('div');
  recentMenu.className = 'ai-toolbar-recent-menu';
  recentMenu.setAttribute('role', 'menu');
  recentMenu.setAttribute('aria-label', 'Recent instructions');

  // Append input elements to instruct container
  instructContainer.appendChild(instructInput);
  instructContainer.appendChild(recentBtn);
  instructContainer.appendChild(instructSendBtn);
  instructContainer.appendChild(instructCancelBtn);
  toolbarContainer.appendChild(instructContainer);
  toolbarContainer.appendChild(recentMenu);

  // Visible op-button row (single user-ordered list; overflow moves to the dropdown)
  const primaryContainer = document.createElement('div');
  primaryContainer.className = 'ai-toolbar-primary';
  primaryContainer.style.cssText = 'display: flex; flex-wrap: nowrap; overflow-x: auto; overflow-y: hidden; gap: 6px; flex: 1 1 auto; min-width: 0;';

  const opButtons = new Map<string, HTMLButtonElement>();
  let instructBtn: HTMLButtonElement | null = null;
  let buttonOrder: string[] = [];

  function buildToolbarButtons() {
    primaryContainer.replaceChildren();
    opButtons.clear();
    instructBtn = null;
    buttonOrder = [];
    for (const def of resolveToolbarButtons(toolbarConfig)) {
      buttonOrder.push(def.id);
      if (def.id === 'instruct') {
        const btn = document.createElement('button');
        btn.className = 'ai-toolbar-btn ai-toolbar-btn-instruct';
        btn.dataset.opId = def.id;
        btn.innerHTML = `<span style="margin-right: 4px;">${def.icon}</span>${def.label}`;
        btn.style.cssText = `
          display: flex;
          align-items: center;
          padding: 6px 12px;
          font-size: 13px;
          font-weight: 500;
          color: white;
          background: ${def.color};
          border: none;
          border-radius: 6px;
          cursor: pointer;
          opacity: 0.5;
          pointer-events: none;
          transition: opacity 0.2s, transform 0.1s;
          white-space: nowrap;
          flex-shrink: 0;
        `;
        btn.addEventListener('mousedown', (e) => {
          e.preventDefault(); // Prevent editor losing focus
        });
        btn.addEventListener('click', () => {
          isInstructMode = true;
          updateState();
          instructInput.focus();
        });
        instructBtn = btn;
        opButtons.set(def.id, btn);
        primaryContainer.appendChild(btn);
        continue;
      }
      const btn = createButton(def);
      opButtons.set(def.id, btn);
      primaryContainer.appendChild(btn);
    }
  }

  buildToolbarButtons();

  toolbarContainer.appendChild(primaryContainer);

  // Handle instruct send
  const sendInstruct = () => {
    const prompt = instructInput.value.trim();
    if (!prompt || hasSamplerError) return;
    rememberInstruction(prompt);

    // Store the prompt in state for error recovery
    state.instructPrompt = prompt;
    isInstructMode = false;
    // Call the action with instruct operation and custom prompt
    // If no selection, pass empty text and use current cursor position
    const text = hasSelection ? selectedText : '';
    const selection = currentSelection ?? view.state.selection.main;
    onAction('instruct', text, selection, prompt);

    // Reset UI
    instructInput.value = '';
    instructInput.style.height = '';
    updateState();
    // Leave the instruct textarea so shortcuts work during generation
    scheduleFocusWork(() => ensureEditorFocus());
  };

  // Esc / Cancel only hide the box; the draft stays for the next time Custom opens
  const closeInstruct = () => {
    isInstructMode = false;
    updateState();
    view.focus();
  };

  // An instruction edited in the reopened Custom box replaces the original
  const retry = () => {
    let editedPrompt: string | undefined;
    if (isInstructMode && state.currentOperation === 'instruct') {
      editedPrompt = instructInput.value.trim() || undefined;
      if (editedPrompt) rememberInstruction(editedPrompt);
      if (document.activeElement === instructInput) view.focus();
      isInstructMode = false;
      instructInput.value = '';
      instructInput.style.height = '';
    }
    onRetry(editedPrompt);
  };

  instructSendBtn.addEventListener('click', sendInstruct);
  instructInput.addEventListener('keydown', (e) => {
    if (isImeComposing(e)) return;
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendInstruct();
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      if (recentMenu.style.display === 'flex') closeRecentMenu();
      else closeInstruct();
    }
    if ((e.key === 'ArrowUp' || e.key === 'ArrowDown') && !e.shiftKey && !e.altKey && !e.ctrlKey && !e.metaKey) {
      if (stepRecent(e.key === 'ArrowUp' ? 1 : -1)) e.preventDefault();
    }
  });

  let recentIndex = -1;

  function setInstructValue(value: string) {
    instructInput.value = value;
    resizeInstructInput();
    instructInput.setSelectionRange(value.length, value.length);
  }

  // Shell-style recall: ↑ in an empty box steps back through recent instructions, ↓ comes forward
  function stepRecent(direction: 1 | -1): boolean {
    const recent = getRecentInstructions();
    const browsing = recentIndex >= 0 && instructInput.value === recent[recentIndex];
    if (!browsing) {
      recentIndex = -1;
      if (direction < 0 || instructInput.value !== '' || recent.length === 0) return false;
    }
    const next = Math.min(recentIndex + direction, recent.length - 1);
    recentIndex = next;
    setInstructValue(next < 0 ? '' : recent[next]);
    return true;
  }

  function syncRecentButton() {
    recentBtn.style.display = getRecentInstructions().length > 0 ? 'flex' : 'none';
  }

  let clearAllTimer = 0;
  let clearAllBtn: HTMLButtonElement | null = null;

  function disarmClearAll() {
    window.clearTimeout(clearAllTimer);
    clearAllTimer = 0;
    if (!clearAllBtn) return;
    clearAllBtn.classList.remove('is-armed');
    clearAllBtn.textContent = 'Clear all';
    clearAllBtn.title = 'Clear all recent instructions';
  }

  function closeRecentMenu() {
    disarmClearAll();
    recentMenu.style.display = 'none';
    recentBtn.setAttribute('aria-expanded', 'false');
  }

  // Two-step like message delete: the first click arms, a second click within 4s clears
  function createClearAllButton(): HTMLButtonElement {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'ai-toolbar-recent-clear';
    btn.textContent = 'Clear all';
    btn.title = 'Clear all recent instructions';
    btn.addEventListener('mousedown', (e) => e.preventDefault());
    btn.addEventListener('click', () => {
      if (!btn.classList.contains('is-armed')) {
        btn.classList.add('is-armed');
        btn.textContent = 'Confirm clear';
        btn.title = 'Click again to clear all recent instructions';
        clearAllTimer = window.setTimeout(disarmClearAll, 4000);
        return;
      }
      saveRecentInstructions([]);
      closeRecentMenu();
      syncRecentButton();
      instructInput.focus();
    });
    return btn;
  }

  function openRecentMenu() {
    const recent = getRecentInstructions();
    if (recent.length === 0) return;
    const header = document.createElement('div');
    header.className = 'ai-toolbar-recent-header';
    const title = document.createElement('span');
    title.className = 'ai-toolbar-recent-title';
    title.textContent = 'Recent instructions';
    disarmClearAll();
    clearAllBtn = createClearAllButton();
    header.append(title, clearAllBtn);
    recentMenu.replaceChildren(header);
    for (const prompt of recent) {
      const row = document.createElement('div');
      row.className = 'ai-toolbar-recent-item';
      const use = document.createElement('button');
      use.type = 'button';
      use.className = 'ai-toolbar-recent-use';
      use.setAttribute('role', 'menuitem');
      use.textContent = prompt;
      use.title = prompt;
      use.addEventListener('mousedown', (e) => e.preventDefault());
      use.addEventListener('click', () => {
        recentIndex = -1;
        closeRecentMenu();
        setInstructValue(prompt);
        instructInput.focus();
      });
      const remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'ai-toolbar-recent-remove';
      remove.textContent = '✕';
      remove.title = 'Remove from recent';
      remove.setAttribute('aria-label', `Remove "${prompt}" from recent instructions`);
      remove.addEventListener('mousedown', (e) => e.preventDefault());
      remove.addEventListener('click', () => {
        forgetInstruction(prompt);
        row.remove();
        if (getRecentInstructions().length === 0) {
          closeRecentMenu();
          syncRecentButton();
          instructInput.focus();
        }
      });
      row.append(use, remove);
      recentMenu.appendChild(row);
    }
    recentMenu.style.display = 'flex';
    recentBtn.setAttribute('aria-expanded', 'true');
  }

  recentBtn.addEventListener('mousedown', (e) => e.preventDefault());
  recentBtn.addEventListener('click', () => {
    if (recentMenu.style.display === 'flex') closeRecentMenu();
    else openRecentMenu();
  });
  recentMenu.addEventListener('pointerdown', (e) => {
    if (clearAllBtn && !e.composedPath().includes(clearAllBtn)) disarmClearAll();
  });
  recentMenu.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    e.preventDefault();
    e.stopPropagation();
    closeRecentMenu();
    instructInput.focus();
  });

  function resizeInstructInput() {
    instructInput.style.height = 'auto';
    const newHeight = Math.min(instructInput.scrollHeight, 120);
    instructInput.style.height = `${newHeight}px`;
  }

  instructInput.addEventListener('input', () => {
    recentIndex = -1;
    resizeInstructInput();
  });

  instructCancelBtn.addEventListener('click', closeInstruct);

  // Add "More" dropdown
  const moreBtn = document.createElement('button');
  moreBtn.className = 'ai-toolbar-btn-more';
  moreBtn.innerHTML = '▼ More';
  moreBtn.style.cssText = `
    display: flex;
    align-items: center;
    padding: 6px 10px;
    font-size: 12px;
    font-weight: 500;
    color: var(--ai-toolbar-text-secondary);
    background: transparent;
    border: 1px solid var(--ai-toolbar-input-border);
    border-radius: 6px;
    cursor: pointer;
    opacity: 0.5;
    pointer-events: none;
  `;

  // Dropdown menu — stacked above search panel via CSS (see index.css)
  const dropdown = document.createElement('div');
  dropdown.className = 'ai-toolbar-more-dropdown';

  const moreContainer = document.createElement('div');
  moreContainer.className = 'ai-toolbar-more';
  moreContainer.style.display = 'none';
  moreContainer.style.flexShrink = '0';
  moreContainer.appendChild(moreBtn);
  moreContainer.appendChild(dropdown);
  toolbarContainer.appendChild(moreContainer);

  // Utilities live in their own shrink-proof group so op buttons never crowd them.
  // The op row takes whatever space is left and overflows into the More menu.
  const utilitiesContainer = document.createElement('div');
  utilitiesContainer.className = 'ai-toolbar-utilities';
  utilitiesContainer.style.cssText = 'display: flex; align-items: center; gap: 6px; flex-shrink: 0;';
  toolbarContainer.appendChild(utilitiesContainer);

  /**
   * Auto-overflow: keep leading buttons in the row while they fit; move the
   * rest (in order) into the More dropdown. The pinned Custom button
   * (instruct) never overflows. More is only shown while it holds buttons.
   */
  function relayoutToolbar() {
    if (panelDestroyed) return;
    if (primaryContainer.style.display === 'none') return;
    const wasOpen = dropdown.style.display === 'flex';
    for (const id of buttonOrder) {
      const btn = opButtons.get(id);
      if (btn) primaryContainer.appendChild(btn);
    }
    dropdown.replaceChildren();
    dropdown.style.display = 'none';
    if (!toolbarContainer.clientWidth) {
      moreContainer.style.display = 'none';
      return;
    }
    // Reserve the More slot while measuring so the row width is final
    moreContainer.style.display = 'block';
    moreContainer.style.visibility = 'hidden';
    let guard = opButtons.size + 1;
    while (guard-- > 0 && primaryContainer.scrollWidth > primaryContainer.clientWidth + 1) {
      const rowBtns = [...primaryContainer.children] as HTMLElement[];
      const movable = rowBtns.filter((el) => el.dataset.opId !== 'instruct');
      if (movable.length === 0) break;
      const last = movable[movable.length - 1];
      if (!last) break;
      dropdown.prepend(last);
    }
    moreContainer.style.visibility = '';
    moreContainer.style.display = dropdown.childElementCount > 0 ? 'block' : 'none';
    if (wasOpen && dropdown.childElementCount > 0) {
      dropdown.style.display = 'flex';
    }
  }

  const toolbarResizeObserver =
    typeof ResizeObserver !== 'undefined' ? new ResizeObserver(() => relayoutToolbar()) : null;
  toolbarResizeObserver?.observe(toolbarContainer);

  // Search button (magnifying glass)
  const searchBtn = document.createElement('button');
  searchBtn.className = 'ai-toolbar-btn-search';
  searchBtn.innerHTML = '🔍';
  searchBtn.title = 'Search & Replace (Ctrl+F)';
  searchBtn.setAttribute('aria-label', 'Search and replace');
  searchBtn.style.cssText = `
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 6px 10px;
    font-size: 14px;
    background: transparent;
    border: 1px solid var(--ai-toolbar-input-border);
    border-radius: 6px;
    cursor: pointer;
    color: var(--ai-toolbar-text-secondary);
    transition: all 0.15s ease;
    margin-left: 4px;
  `;
  searchBtn.addEventListener('click', () => {
    toggleToolbarSearch(view);
  });
  searchBtn.addEventListener('mousedown', (e) => {
    e.preventDefault();
  });

  // Update search button appearance based on search panel state (matches aA button styling)
  const updateSearchButtonState = () => {
    const isOpen = view.state.field(searchPanelOpen);
    if (isOpen) {
      searchBtn.style.background = 'var(--ai-toolbar-active-bg)';
      searchBtn.style.borderColor = 'var(--ai-toolbar-active)';
      searchBtn.style.color = 'var(--ai-toolbar-active)';
    } else {
      searchBtn.style.background = 'transparent';
      searchBtn.style.borderColor = 'var(--ai-toolbar-input-border)';
      searchBtn.style.color = 'var(--ai-toolbar-text-secondary)';
    }
  };

  // Set initial state
  updateSearchButtonState();

  // Listen for search panel state changes - defer dispatch to avoid "update in progress" error
  const searchPanelListener = EditorView.updateListener.of((update) => {
    const wasOpen = update.startState.field(searchPanelOpen);
    const isOpen = update.state.field(searchPanelOpen);
    if (wasOpen !== isOpen) {
      updateSearchButtonState();
    }
  });
  
  // Defer the dispatch to avoid "update in progress" error
  const searchPanelTimer = setTimeout(() => {
    view.dispatch({ effects: StateEffect.appendConfig.of([searchPanelListener]) });
  }, 0);

  utilitiesContainer.appendChild(searchBtn);

  // Non-payload utilities are hidden during Custom-instruct mode while the
  // payload preflight button stays reachable next to the instruction input.
  const nonPayloadUtilities: Array<{ el: HTMLElement; showDisplay: string }> = [
    { el: searchBtn, showDisplay: 'flex' },
  ];

  // Payload preflight button (view request body without sending)
  const payloadBtn = document.createElement('button');
  payloadBtn.className = 'ai-toolbar-btn-payload';
  payloadBtn.innerHTML = '{ }';
  payloadBtn.title = 'Preview AI request payload';
  payloadBtn.style.cssText = `
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 6px 10px;
    font-size: 12px;
    font-weight: 600;
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
    background: transparent;
    border: 1px solid var(--ai-toolbar-input-border);
    border-radius: 6px;
    cursor: pointer;
    color: var(--ai-toolbar-text-secondary);
    transition: all 0.15s ease;
    margin-left: 4px;
    opacity: 0.5;
    pointer-events: none;
  `;
  payloadBtn.addEventListener('mousedown', (e) => {
    e.preventDefault();
  });
  payloadBtn.addEventListener('click', () => {
    if (!onPreviewPayload || !hasSelection || !currentSelection) return;
    onPreviewPayload({
      text: selectedText,
      selection: currentSelection,
      defaultOperation: isInstructMode ? 'instruct' : 'expand',
      instructDraft: isInstructMode ? instructInput.value : undefined,
    });
  });
  if (onPreviewPayload) {
    utilitiesContainer.appendChild(payloadBtn);
  }

  for (const action of toolbarActions) {
    const button = document.createElement('button');
    button.className = `ai-toolbar-btn-custom ai-toolbar-btn-custom-${action.id}`;
    button.textContent = action.label;
    button.title = action.title ?? action.label;
    button.style.cssText = `
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 6px 10px;
      font-size: 14px;
      font-weight: 600;
      background: transparent;
      border: 1px solid var(--ai-toolbar-input-border);
      border-radius: 6px;
      cursor: pointer;
      color: var(--ai-toolbar-text-secondary);
      transition: all 0.15s ease;
      margin-left: 4px;
      font-family: ui-sans-serif, system-ui, sans-serif;
    `;
    button.addEventListener('click', () => {
      action.onClick(view);
    });
    button.addEventListener('mousedown', (e) => {
      e.preventDefault();
    });
    utilitiesContainer.appendChild(button);
    nonPayloadUtilities.push({ el: button, showDisplay: 'flex' });
  }

  // Font size control (aA button with slider popup)
  let fontSizeCleanup: (() => void) | undefined;
  if (onFontSizeChange) {
    const { button: fontSizeBtnContainer, cleanup } = createFontSizeControl(view, onFontSizeChange);
    utilitiesContainer.appendChild(fontSizeBtnContainer);
    nonPayloadUtilities.push({ el: fontSizeBtnContainer, showDisplay: '' });
    fontSizeCleanup = cleanup;
  }

  // Abort button (shown during processing)
  const abortBtn = document.createElement('button');
  abortBtn.className = 'ai-toolbar-btn-abort';
  abortBtn.innerHTML = '⏹ Stop';
  abortBtn.title = 'Stop AI operation (Escape)';
  abortBtn.style.cssText = `
    display: none;
    align-items: center;
    padding: 6px 12px;
    font-size: 13px;
    font-weight: 500;
    color: white;
    background: var(--danger);
    border: none;
    border-radius: 6px;
    cursor: pointer;
    transition: background 0.2s;
  `;
  abortBtn.addEventListener('click', () => {
    onAbort();
  });
  abortBtn.addEventListener('mousedown', (e) => {
    e.preventDefault();
  });
  toolbarContainer.appendChild(abortBtn);

  // Toggle dropdown
  moreBtn.addEventListener('click', () => {
    if (!hasSelection) return;
    if (dropdown.childElementCount === 0) return;
    const isOpen = dropdown.style.display === 'flex';
    dropdown.style.display = isOpen ? 'none' : 'flex';
  });

  moreBtn.addEventListener('mousedown', (e) => {
    e.preventDefault();
  });

  // Close dropdown when clicking outside
  const closeDropdownOnOutsideClick = (e: MouseEvent) => {
    if (!moreContainer.contains(e.target as Node)) {
      dropdown.style.display = 'none';
    }
    const path = e.composedPath();
    if (!path.includes(recentMenu) && !path.includes(recentBtn)) closeRecentMenu();
  };
  document.addEventListener('click', closeDropdownOnOutsideClick);

  // Separator
  const separator = document.createElement('div');
  separator.className = 'ai-toolbar-separator';
  separator.style.cssText = 'width: 1px; height: 24px; background: var(--ai-toolbar-border); margin: 0 4px;';
  toolbarContainer.appendChild(separator);

  // Selection info
  const infoText = document.createElement('span');
  infoText.className = 'ai-toolbar-info';
  infoText.textContent = 'Select text to use AI';
  infoText.style.cssText = `
    font-size: 12px;
    color: var(--ai-toolbar-text-muted);
    margin-left: auto;
  `;
  toolbarContainer.appendChild(infoText);

  // Result container content elements
  const resultHeader = document.createElement('div');
  resultHeader.style.cssText = `
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 4px 8px;
    margin-bottom: 8px;
  `;
  resultContainer.appendChild(resultHeader);

  const resultTitle = document.createElement('span');
  resultTitle.style.cssText = `
    font-size: 12px;
    font-weight: 600;
    color: var(--ai-toolbar-text-secondary);
    text-transform: uppercase;
    letter-spacing: 0.5px;
  `;
  resultHeader.appendChild(resultTitle);

  const cutOffNote = document.createElement('span');
  cutOffNote.textContent = '⚠ Cut off at Max Tokens';
  cutOffNote.title = 'The model hit the Max Tokens limit, so the text may end mid-sentence. Raise Max Tokens in the sampler settings and retry.';
  cutOffNote.style.cssText = `
    display: none;
    white-space: nowrap;
    font-size: 12px;
    font-weight: 500;
    color: var(--warning);
  `;
  resultHeader.appendChild(cutOffNote);

  // Model + timing pills stay on one right-aligned row; on narrow screens the model name shortens
  const resultMeta = document.createElement('div');
  resultMeta.style.cssText = `
    display: none;
    align-items: center;
    gap: 6px;
    min-width: 0;
    margin-left: auto;
  `;
  resultHeader.appendChild(resultMeta);

  const modelPill = createStatPill(MODEL_ICON_PATHS, 'var(--ai-toolbar-text-secondary)');
  modelPill.pill.style.maxWidth = '200px';
  modelPill.pill.style.minWidth = '72px';
  const ttftPill = createStatPill(TIMER_ICON_PATHS, 'var(--ai-toolbar-text-muted)');
  ttftPill.pill.title = 'Time to first token';
  ttftPill.pill.style.flexShrink = '0';
  const speedPill = createStatPill(GAUGE_ICON_PATHS, 'var(--ai-toolbar-text-muted)');
  speedPill.pill.title = 'Speed, in estimated tokens per second';
  speedPill.pill.style.flexShrink = '0';
  resultMeta.append(modelPill.pill, ttftPill.pill, speedPill.pill);

  const errorRetryBtn = document.createElement('button');
  errorRetryBtn.textContent = '↻ Retry';
  errorRetryBtn.title = 'Run the same request again';
  errorRetryBtn.style.cssText = `
    display: none;
    margin-left: auto;
    padding: 2px 8px;
    font-size: 12px;
    font-weight: 500;
    color: var(--ai-toolbar-text-secondary);
    background: transparent;
    border: 1px solid var(--ai-toolbar-input-border);
    border-radius: 4px;
    cursor: pointer;
  `;
  errorRetryBtn.addEventListener('mousedown', (e) => {
    e.preventDefault();
  });
  errorRetryBtn.addEventListener('click', () => retry());
  resultHeader.appendChild(errorRetryBtn);

  const resultCloseBtn = document.createElement('button');
  resultCloseBtn.innerHTML = '✕';
  resultCloseBtn.title = 'Dismiss error (Escape)';
  resultCloseBtn.style.cssText = `
    display: none;
    padding: 2px 6px;
    font-size: 14px;
    color: var(--ai-toolbar-text-muted);
    background: transparent;
    border: none;
    cursor: pointer;
    border-radius: 4px;
  `;
  resultCloseBtn.addEventListener('click', () => {
    onReject();
  });
  resultHeader.appendChild(resultCloseBtn);

  // Error display
  const errorDisplay = document.createElement('div');
  errorDisplay.style.cssText = `
    display: none;
    padding: 8px 12px;
    background: var(--ai-toolbar-error-bg);
    border: 1px solid var(--ai-toolbar-error-border);
    border-radius: 6px;
    color: var(--ai-toolbar-error-text);
    font-size: 13px;
    margin-bottom: 8px;
  `;
  resultContainer.appendChild(errorDisplay);

  // Compact processing indicator (body text streams as an in-editor ghost)
  const processingIndicator = document.createElement('div');
  processingIndicator.style.cssText = `
    display: none;
    flex-direction: row;
    align-items: center;
    justify-content: flex-start;
    padding: 4px 0 8px;
    gap: 8px;
  `;
  processingIndicator.innerHTML = `
    <div style="
      width: 14px;
      height: 14px;
      border: 2px solid var(--ai-toolbar-input-border);
      border-top-color: var(--ai-toolbar-text-muted);
      border-radius: 50%;
      animation: ai-spin 1s linear infinite;
      flex-shrink: 0;
    "></div>
    <span style="font-size: 12px; color: var(--ai-toolbar-text-muted);">Writing in editor…</span>
    <style>
      @keyframes ai-spin {
        to { transform: rotate(360deg); }
      }
    </style>
  `;
  resultContainer.appendChild(processingIndicator);

  const reasoningFold = document.createElement('details');
  reasoningFold.className = 'ai-thinking-fold';
  reasoningFold.style.cssText = `
    display: none;
    margin-bottom: 8px;
    flex-shrink: 0;
    color: var(--ai-toolbar-text-muted);
    font-size: 12px;
  `;
  resultContainer.appendChild(reasoningFold);

  const reasoningStyle = document.createElement('style');
  reasoningStyle.textContent = `
    .ai-thinking-fold > summary { list-style: none; }
    .ai-thinking-fold > summary::-webkit-details-marker { display: none; }
    .ai-thinking-fold[open] > summary .thinking-chevron { transform: rotate(90deg); }
  `;
  reasoningFold.appendChild(reasoningStyle);

  const reasoningSummary = document.createElement('summary');
  reasoningSummary.style.cssText = `
    display: flex;
    align-items: center;
    gap: 4px;
    cursor: pointer;
    color: var(--ai-toolbar-text-muted);
  `;
  const reasoningChevron = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  reasoningChevron.setAttribute('viewBox', '0 0 24 24');
  reasoningChevron.setAttribute('fill', 'none');
  reasoningChevron.setAttribute('stroke', 'currentColor');
  reasoningChevron.setAttribute('stroke-width', '2');
  reasoningChevron.setAttribute('stroke-linecap', 'round');
  reasoningChevron.setAttribute('stroke-linejoin', 'round');
  reasoningChevron.setAttribute('aria-hidden', 'true');
  reasoningChevron.classList.add('thinking-chevron');
  reasoningChevron.style.cssText = 'width: 12px; height: 12px; flex-shrink: 0; transition: transform 0.15s;';
  const reasoningChevronPath = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  reasoningChevronPath.setAttribute('d', 'm9 18 6-6-6-6');
  reasoningChevron.appendChild(reasoningChevronPath);
  reasoningSummary.appendChild(reasoningChevron);
  const reasoningLabel = document.createElement('span');
  reasoningLabel.textContent = 'Thinking';
  reasoningSummary.appendChild(reasoningLabel);
  reasoningFold.appendChild(reasoningSummary);

  const reasoningContent = document.createElement('div');
  reasoningContent.style.cssText = `
    max-height: 160px;
    overflow-y: auto;
    margin-top: 4px;
    padding-left: 16px;
    font-size: 12px;
    line-height: 1.625;
    white-space: pre-wrap;
    word-break: break-word;
    color: var(--ai-toolbar-text-muted);
  `;
  reasoningFold.appendChild(reasoningContent);

  const reasoningText = document.createElement('div');
  reasoningContent.appendChild(reasoningText);

  // Action buttons container (AI body is previewed in-editor as a ghost)
  const actionButtons = document.createElement('div');
  actionButtons.style.cssText = `
    display: none;
    gap: 8px;
    margin-top: 4px;
  `;
  resultContainer.appendChild(actionButtons);

  const isMacPlatform =
    typeof navigator !== 'undefined' &&
    (/Mac|iPhone|iPad|iPod/i.test(navigator.platform) ||
      // navigator.platform is deprecated; userAgentData is not everywhere yet
      (typeof navigator.userAgent === 'string' && /Mac OS X|Macintosh/i.test(navigator.userAgent)));
  const modShortcutLabel = isMacPlatform ? '⌘' : 'Ctrl';

  const acceptBtn = document.createElement('button');
  acceptBtn.innerHTML = '✓ Accept';
  acceptBtn.title = `Accept (${modShortcutLabel}+Enter)`;
  acceptBtn.setAttribute('aria-keyshortcuts', 'Control+Enter Meta+Enter');
  acceptBtn.style.cssText = `
    flex: 1;
    padding: 8px 16px;
    font-size: 13px;
    font-weight: 500;
    color: white;
    background: var(--ai-toolbar-accept-bg);
    border: none;
    border-radius: 6px;
    cursor: pointer;
    transition: background 0.2s;
  `;
  acceptBtn.addEventListener('click', () => {
    onAccept();
    scheduleFocusWork(() => ensureEditorFocus());
  });
  acceptBtn.addEventListener('mousedown', (e) => {
    // Keep / restore editor focus so click + keyboard stay consistent
    e.preventDefault();
    ensureEditorFocus();
  });
  actionButtons.appendChild(acceptBtn);

  const rejectBtn = document.createElement('button');
  rejectBtn.innerHTML = '✕ Reject';
  rejectBtn.title = 'Reject (Escape)';
  rejectBtn.setAttribute('aria-keyshortcuts', 'Escape');
  rejectBtn.style.cssText = `
    flex: 1;
    padding: 8px 16px;
    font-size: 13px;
    font-weight: 500;
    color: var(--ai-toolbar-text);
    background: var(--ai-toolbar-btn-hover);
    border: none;
    border-radius: 6px;
    cursor: pointer;
    transition: background 0.2s;
  `;
  rejectBtn.addEventListener('click', () => {
    onReject();
    scheduleFocusWork(() => ensureEditorFocus());
  });
  rejectBtn.addEventListener('mousedown', (e) => {
    e.preventDefault();
    ensureEditorFocus();
  });
  actionButtons.appendChild(rejectBtn);

  const retryBtn = document.createElement('button');
  retryBtn.textContent = '↻ Retry';
  retryBtn.title = 'Discard this result and run the same request again';
  retryBtn.style.cssText = `
    flex: 0 0 auto;
    padding: 8px 12px;
    font-size: 13px;
    font-weight: 500;
    color: var(--ai-toolbar-text-secondary);
    background: transparent;
    border: 1px solid var(--ai-toolbar-input-border);
    border-radius: 6px;
    cursor: pointer;
  `;
  retryBtn.addEventListener('mousedown', (e) => {
    e.preventDefault();
    ensureEditorFocus();
  });
  retryBtn.addEventListener('click', () => retry());
  actionButtons.appendChild(retryBtn);

  // Shortcuts for this editor's AI session — work even when focus fell to <body>
  // after the processing UI hid the button that was focused.
  const onPanelKeyDown = (e: KeyboardEvent) => {
    if (panelDestroyed) return;
    const target = e.target as Node | null;

    // Don't steal Escape from the custom-instruction textarea (local cancel)
    if (
      e.key === 'Escape' &&
      target instanceof HTMLElement &&
      (target === instructInput || instructInput.contains(target))
    ) {
      return;
    }

    if (!shouldHandleAiShortcuts()) return;

    // Search panel owns Escape first
    if (e.key === 'Escape' && view.state.field(searchPanelOpen, false)) {
      e.preventDefault();
      e.stopPropagation();
      closeToolbarSearch(view);
      return;
    }

    const canAccept =
      !state.isProcessing && !state.isStreaming && !!state.aiResult && !state.error;
    const canDismiss =
      canAccept ||
      !!state.error ||
      state.isProcessing ||
      state.isStreaming;

    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter' && canAccept) {
      e.preventDefault();
      e.stopPropagation();
      onAccept();
      scheduleFocusWork(() => ensureEditorFocus());
      return;
    }

    if (e.key === 'Escape' && canDismiss) {
      // Prefer abort while running; reject/dismiss when done or on error
      e.preventDefault();
      e.stopPropagation();
      if (state.isProcessing || state.isStreaming) {
        onAbort();
      } else {
        onReject();
      }
      scheduleFocusWork(() => ensureEditorFocus());
    }
  };
  // Capture phase: still fire when focus is body or panel controls
  document.addEventListener('keydown', onPanelKeyDown, true);

  // Autoscroll only when the user has expanded thinking. Stickiness is
  // position-based so scrolling back to the bottom resumes following, and
  // pins are coalesced (never cancelled-and-rescheduled) so fast token
  // bursts can't starve the scroll.
  const THINKING_STICK_THRESHOLD = 24;
  let thinkingStuck = true;
  let thinkingScrollRaf = 0;
  let resultStuck = true;

  function thinkingBodyText(): string {
    if (state.isStreaming) return clipLiveReasoning(state.streamingReasoning);
    return state.aiReasoning;
  }

  function syncThinkingDom() {
    if (!reasoningFold.open) {
      reasoningText.textContent = '';
      return;
    }
    reasoningText.textContent = thinkingBodyText();
  }

  function pinThinkingToBottom() {
    if (panelDestroyed || !reasoningFold.open || !thinkingStuck) return;
    reasoningContent.scrollTop = reasoningContent.scrollHeight;
  }

  function scheduleThinkingPin() {
    if (panelDestroyed || !reasoningFold.open || !thinkingStuck) return;
    if (thinkingScrollRaf) return;
    thinkingScrollRaf = requestAnimationFrame(() => {
      thinkingScrollRaf = 0;
      if (panelDestroyed) return;
      pinThinkingToBottom();
      if (resultStuck) resultContainer.scrollTop = resultContainer.scrollHeight;
    });
  }

  const onReasoningScroll = () => {
    thinkingStuck =
      reasoningContent.scrollTop + reasoningContent.clientHeight >=
      reasoningContent.scrollHeight - THINKING_STICK_THRESHOLD;
  };

  const onResultScroll = () => {
    resultStuck =
      resultContainer.scrollTop + resultContainer.clientHeight >=
      resultContainer.scrollHeight - THINKING_STICK_THRESHOLD;
  };

  const onReasoningToggle = () => {
    syncThinkingDom();
    if (reasoningFold.open) {
      thinkingStuck = true;
      reasoningContent.scrollTop = reasoningContent.scrollHeight;
      scheduleThinkingPin();
    }
  };

  reasoningContent.addEventListener('scroll', onReasoningScroll);
  resultContainer.addEventListener('scroll', onResultScroll);
  reasoningFold.addEventListener('toggle', onReasoningToggle);

  // Update result chrome based on state (body text is an in-editor ghost)
  function updateResultDisplay() {
    const hasContent = state.isProcessing || state.isStreaming || state.aiResult || state.error;

    if (!hasContent) {
      resultContainer.style.display = 'none';
      return;
    }

    resultContainer.style.display = 'flex';

    // Update title / one-line status
    const opLabel = state.currentOperation
      ? toolbarButtonLabel(state.currentOperation, toolbarConfig.customOps)
      : 'AI Result';
    if (state.error) {
      resultTitle.textContent = opLabel;
    } else if (state.isStreaming) {
      resultTitle.textContent = `${opLabel} · streaming…`;
    } else if (state.isProcessing) {
      resultTitle.textContent = `${opLabel} · working…`;
    } else if (state.aiResult) {
      resultTitle.textContent = `${opLabel} · ready`;
    } else {
      resultTitle.textContent = opLabel;
    }

    // Error display
    if (state.error) {
      errorDisplay.style.display = 'block';
      errorDisplay.textContent = state.error;
      processingIndicator.style.display = 'none';
      reasoningFold.style.display = 'none';
      reasoningText.textContent = '';
      resultMeta.style.display = 'none';
      cutOffNote.style.display = 'none';
      errorRetryBtn.style.display = 'inline';
      resultCloseBtn.style.display = 'inline';
      actionButtons.style.display = 'none';
      return;
    } else {
      errorDisplay.style.display = 'none';
      errorRetryBtn.style.display = 'none';
      resultCloseBtn.style.display = 'none';
    }

    // Compact processing row until first stream tokens / while waiting
    processingIndicator.style.display =
      state.isProcessing && !state.isStreaming && !state.aiResult ? 'flex' : 'none';

    const hasReasoning =
      (state.streamingReasoning?.trim()?.length ?? 0) > 0 ||
      (state.aiReasoning?.trim()?.length ?? 0) > 0;
    reasoningFold.style.display = hasReasoning ? 'block' : 'none';
    if (hasReasoning) {
      syncThinkingDom();

      if (reasoningFold.open && (state.isStreaming ? state.streamingReasoning : state.aiReasoning)) {
        pinThinkingToBottom();
        scheduleThinkingPin();
      }
    } else {
      reasoningText.textContent = '';
    }

    // Stats display (only when complete and has result)
    const hasStats = state.stats && !state.isProcessing && !state.isStreaming && state.aiResult && !state.error;
    const s = hasStats ? state.stats! : null;
    const modelId = s?.modelId?.trim();
    if (modelId) {
      modelPill.label.textContent = shortModelName(modelId);
      modelPill.pill.title = s!.providerId ? `Model: ${modelId}\nProvider: ${s!.providerId}` : `Model: ${modelId}`;
    }
    modelPill.pill.style.display = modelId ? 'inline-flex' : 'none';
    const ttft = s?.ttft;
    if (typeof ttft === 'number') {
      ttftPill.label.textContent = ttft < 1000 ? `${Math.round(ttft)}ms` : `${(ttft / 1000).toFixed(1)}s`;
    }
    ttftPill.pill.style.display = typeof ttft === 'number' ? 'inline-flex' : 'none';
    const tokensPerSecond = s?.tokensPerSecond;
    if (typeof tokensPerSecond === 'number') {
      speedPill.label.textContent = `${tokensPerSecond.toFixed(1)} t/s`;
    }
    speedPill.pill.style.display = typeof tokensPerSecond === 'number' ? 'inline-flex' : 'none';
    resultMeta.style.display = hasStats ? 'flex' : 'none';
    cutOffNote.style.display = hasStats && state.stats!.truncated ? 'inline' : 'none';

    // Action buttons (only when complete and has result)
    const showActions = !state.isProcessing && !state.isStreaming && state.aiResult && !state.error;
    actionButtons.style.display = showActions ? 'flex' : 'none';

    if (!state.isStreaming) {
      thinkingStuck = true;
    }
  }

  // The Send label names what Custom will do: rewrite the selection or insert at the cursor
  function syncInstructTarget(warning: string | null) {
    const label = currentSelection ? 'Rewrite' : 'Insert';
    if (instructSendBtn.textContent !== label) instructSendBtn.textContent = label;
    instructSendBtn.title = warning
      ? warning.replace(/^⚠️\s*/, '')
      : currentSelection
        ? `Rewrite the ${selectedText.length} selected characters with this instruction (Enter)`
        : 'Nothing is selected, so the AI writes new text at the cursor (Enter)';
    instructInput.placeholder = currentSelection
      ? 'How should the selected text change?'
      : 'What should I write at the cursor?';
    instructSendBtn.style.opacity = warning ? '0.5' : '';
    instructSendBtn.style.pointerEvents = warning ? 'none' : '';
  }

  // Update function - called when selection changes
  function updateState() {
    const selection = view.state.selection.main;
    const wasRowHidden = primaryContainer.style.display === 'none';
    hasSelection = selection.from !== selection.to;

    if (hasSelection) {
      currentSelection = selection;
      selectedText = view.state.doc.sliceString(selection.from, selection.to);
    } else {
      currentSelection = null;
      selectedText = '';
      dropdown.style.display = 'none';
    }

    // Sampler problems block every op; a too-long selection only while it is selected
    const limit = Math.max(0, currentSampler.contextLength - currentSampler.maxTokens - SAFETY_MARGIN);
    let warning: string | null = null;
    if (currentSampler.maxTokens >= currentSampler.contextLength) {
      warning = '⚠️ Please adjust Max Tokens to be less than Context Length';
    } else if (limit <= 0) {
      warning = '⚠️ AI needs a larger context window';
    } else if (hasSelection) {
      const estimatedTokens = AIService.estimateTokens(selectedText);
      if (estimatedTokens > limit) {
        warning = `⚠️ Selection is too long (${estimatedTokens}/${limit} tokens)`;
      }
    }

    hasSamplerError = warning !== null;
    if (warning) {
      infoText.textContent = warning;
      infoText.style.color = 'var(--ai-toolbar-error-text)';
      infoText.classList.add('warning');
      hasSelection = false;
    } else {
      infoText.textContent = hasSelection ? `${selectedText.length} chars selected` : 'Select text to use AI';
      infoText.style.color = 'var(--ai-toolbar-text-muted)';
      infoText.classList.remove('warning');
    }
    syncInstructTarget(warning);

    // Show/hide abort button based on processing state
    if (state.isProcessing) {
      abortBtn.style.display = 'flex';
      instructContainer.style.display = 'none';
      primaryContainer.style.display = 'none';
      moreContainer.style.display = 'none';
      utilitiesContainer.style.display = 'none';
      separator.style.display = 'none';
      infoText.style.display = 'none';
    } else if (isInstructMode) {
      abortBtn.style.display = 'none';
      instructContainer.style.display = 'flex';
      primaryContainer.style.display = 'none';
      moreContainer.style.display = 'none';
      // Keep the payload preflight button reachable next to the Custom input;
      // every other utility stays hidden in this compact layout.
      if (onPreviewPayload) {
        utilitiesContainer.style.display = 'flex';
        for (const { el } of nonPayloadUtilities) el.style.display = 'none';
      } else {
        utilitiesContainer.style.display = 'none';
      }
      separator.style.display = 'none';
      infoText.style.display = 'none';
    } else {
      abortBtn.style.display = 'none';
      instructContainer.style.display = 'none';
      primaryContainer.style.display = 'flex';
      moreContainer.style.display = dropdown.childElementCount > 0 ? 'block' : 'none';
      utilitiesContainer.style.display = 'flex';
      for (const { el, showDisplay } of nonPayloadUtilities) el.style.display = showDisplay;
      const showInfo = infoText.classList.contains('warning');
      separator.style.display = showInfo ? 'block' : 'none';
      infoText.style.display = showInfo ? 'block' : 'none';
      // Widths were unmeasurable while hidden (processing / instruct mode)
      if (wasRowHidden) relayoutToolbar();
    }

    const isCompactCustomLayout = !state.isProcessing && isInstructMode;
    toolbarContainer.classList.toggle('ai-toolbar-instruct-mode', isCompactCustomLayout);
    if (isCompactCustomLayout) syncRecentButton();
    else closeRecentMenu();
    if (onPreviewPayload) {
      // Keep available during instruct mode (useful for Custom preflight); hide while processing
      payloadBtn.style.display = state.isProcessing ? 'none' : 'flex';
    }

    // Update all buttons
    // When there's a sampler error, all buttons are disabled including Custom
    // When no sampler error, Custom is always available but other buttons need selection
    const opacity = hasSamplerError ? '0.5' : (hasSelection ? '1' : '0.5');
    const pointerEvents = hasSamplerError ? 'none' : (hasSelection ? 'auto' : 'none');

    for (const btn of opButtons.values()) {
      // Instruct button is always enabled unless there's a sampler error
      if (instructBtn && btn === instructBtn) {
        if (hasSamplerError) {
          btn.style.opacity = '0.5';
          btn.style.pointerEvents = 'none';
        } else {
          btn.style.opacity = '1';
          btn.style.pointerEvents = 'auto';
        }
      } else {
        btn.style.opacity = opacity;
        btn.style.pointerEvents = pointerEvents;
      }
    }

    moreBtn.style.opacity = opacity;
    moreBtn.style.pointerEvents = pointerEvents;

    if (onPreviewPayload) {
      payloadBtn.style.opacity = opacity;
      payloadBtn.style.pointerEvents = pointerEvents;
    }
  }

  // AI state update function
  const updateAIState: AIStreamingCallback = (update) => {
    const wasProcessing = state.isProcessing;
    const hadResult = !!state.aiResult;
    if (update.isProcessing !== undefined) state.isProcessing = update.isProcessing;
    if (update.isStreaming !== undefined) state.isStreaming = update.isStreaming;
    if (update.streamingContent !== undefined) state.streamingContent = update.streamingContent;
    if (update.streamingReasoning !== undefined) state.streamingReasoning = update.streamingReasoning;
    if (update.aiResult !== undefined) state.aiResult = update.aiResult;
    if (update.aiReasoning !== undefined) state.aiReasoning = update.aiReasoning;
    if (update.currentOperation !== undefined) state.currentOperation = update.currentOperation;
    if (update.error !== undefined) state.error = update.error;
    if (update.instructPrompt !== undefined) state.instructPrompt = update.instructPrompt;
    if (update.stats !== undefined) state.stats = update.stats;
    if (update.isProcessing === true) {
      state.stats = null;
      reasoningFold.open = false;
      reasoningText.textContent = '';
    }

    // A Custom op that errored or was rejected sends its instruction back: reopen the box
    let restoredInstructMode = false;
    if (update.instructPrompt) {
      isInstructMode = true;
      instructInput.value = update.instructPrompt;
      restoredInstructMode = true;
      // Clear the stored prompt so we don't re-enter instruct mode on subsequent updates
      state.instructPrompt = null;
    }

    // Update toolbar button visibility when processing state changes or when we restored instruct mode
    if (wasProcessing !== state.isProcessing || restoredInstructMode) {
      updateState();
    }

    updateResultDisplay();

    // After UI swaps (ops hidden / Accept shown), reclaim focus so shortcuts work
    // without requiring a manual click into the editor.
    const startedProcessing = !wasProcessing && state.isProcessing;
    const finishedWithResult =
      wasProcessing &&
      !state.isProcessing &&
      !state.isStreaming &&
      !!state.aiResult &&
      !hadResult;
    const showedError =
      !!update.error && !state.isProcessing && !state.isStreaming;
    if (startedProcessing || finishedWithResult || showedError) {
      scheduleFocusWork(() => ensureEditorFocus());
    }

    // Error recovery: put the user back in the instruct box
    if (restoredInstructMode) {
      scheduleFocusWork(() => {
        if (panelDestroyed) return;
        resizeInstructInput();
        instructInput.focus();
      });
    }
  };

  // Initial state
  updateState();
  // Defer first overflow pass until the panel is attached and measurable
  requestAnimationFrame(() => relayoutToolbar());
  // Webfont swaps can widen buttons after first paint without resizing anything
  if (typeof document !== 'undefined' && typeof document.fonts !== 'undefined') {
    void document.fonts.ready.then(() => relayoutToolbar());
  }

  // Register the update function so it can be accessed from outside
  panelRegistry.set(view, updateAIState);

  // Return panel with update methods
  const panelInstance = {
    dom,
    top: true,
    update: updateState,
    updateState,
    updateAIState,
    updateSampler: (s: SamplerSettings) => {
      currentSampler = s;
      updateState();
    },
    updateToolbarConfig: (c: ToolbarConfig) => {
      toolbarConfig = normalizeToolbarConfig(c);
      isInstructMode = false;
      buildToolbarButtons();
      updateState();
      relayoutToolbar();
    },
    destroy: () => {
      panelDestroyed = true;
      toolbarResizeObserver?.disconnect();
      if (thinkingScrollRaf) cancelAnimationFrame(thinkingScrollRaf);
      thinkingScrollRaf = 0;
      reasoningText.textContent = '';
      for (const id of pendingFocusRafs) {
        cancelAnimationFrame(id);
      }
      pendingFocusRafs.clear();
      document.removeEventListener('click', closeDropdownOnOutsideClick);
      document.removeEventListener('keydown', onPanelKeyDown, true);
      reasoningContent.removeEventListener('scroll', onReasoningScroll);
      resultContainer.removeEventListener('scroll', onResultScroll);
      reasoningFold.removeEventListener('toggle', onReasoningToggle);
      window.clearTimeout(searchPanelTimer);
      // Drop registry entry early so updateAIState closures are not retained
      // solely by the WeakMap until the EditorView is GC'd.
      panelRegistry.delete(view);
      // Break the dom -> panel -> view retain cycle so a lingering dom
      // reference cannot keep the EditorView (and its listeners) alive.
      (dom as unknown as { __panel?: unknown }).__panel = undefined;
      if (fontSizeCleanup) {
        fontSizeCleanup();
      }
    },
  };

  // Store panel instance on DOM for useAIEditor to access
  (dom as unknown as { __panel: typeof panelInstance }).__panel = panelInstance;

  return panelInstance;
}

/**
 * View plugin to track selection changes and update toolbar
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function toolbarPanelPlugin(_onAction: AIToolbarActionCallback) {
  return ViewPlugin.fromClass(
    class {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      constructor(_view: EditorView) {
        // Panel is created by showPanel, we just track updates
      }

      update(update: ViewUpdate) {
        if (update.selectionSet) {
          // Trigger panel update through registry
          const updateFunc = panelRegistry.get(update.view);
          if (updateFunc) {
            // Just trigger a re-render by calling with current state
            // The panel's internal update function handles selection changes
          }
        }
      }
    }
  );
}

/**
 * Create the AI toolbar panel extension
 * This adds a fixed panel at the top of the editor with AI action buttons
 * and result display with streaming support
 */
export function aiToolbarPanel(
  sampler: SamplerSettings,
  onAction: AIToolbarActionCallback,
  onAccept: () => void,
  onReject: () => void,
  onAbort: AIAbortCallback,
  onRetry: AIRetryCallback,
  onFontSizeChange?: FontSizeChangeCallback,
  toolbarActions: ToolbarActionConfig[] = [],
  onPreviewPayload?: AIPreviewPayloadCallback,
  toolbarConfig: ToolbarConfig = DEFAULT_TOOLBAR_CONFIG,
) {
  return [
    showPanel.of((view) =>
      createToolbarPanel(
        view,
        sampler,
        onAction,
        onAccept,
        onReject,
        onAbort,
        onRetry,
        onFontSizeChange,
        toolbarActions,
        onPreviewPayload,
        toolbarConfig
      )
    ),
    toolbarPanelPlugin(onAction),
  ];
}

/**
 * Helper to check if there's a meaningful selection
 */
export function hasSelection(view: EditorView): boolean {
  const sel = view.state.selection.main;
  return sel.from !== sel.to;
}

/**
 * Helper to get selected text
 */
export function getSelectedText(view: EditorView): string {
  const sel = view.state.selection.main;
  return view.state.doc.sliceString(sel.from, sel.to);
}

/**
 * Helper to get current selection range
 */
export function getCurrentSelection(view: EditorView): SelectionRange | null {
  const sel = view.state.selection.main;
  return sel.from !== sel.to ? sel : null;
}
